<script setup>
// What follows a removal that was made to resolve a clash (Figma section
// 4755:112333, "SESSION CONFLICT - PROCESS").
//
// Four states, from two independent facts:
//   • which gesture it was — a plain schedule removal, or releasing a saved seat
//     (which removes the session too, so the copy says both)
//   • whether the removal actually cleared everything, or the attendee still has
//     other items sharing a slot
//
// Only raised when the removed session WAS in conflict. Removing something that
// wasn't clashing is ordinary housekeeping and says nothing.
import { modal_button_filled_2026, modal_button_outline_2026, modal_close_link_2026 } from '~~/shared/utilities/ui';

const props = defineProps({
    sessionTitle: { type: String, default: '' },
    // Which gesture it was, which decides how the sentence is built:
    //   'schedule'          removed from My Schedule, no seat was held
    //   'schedule-and-seat' removed from My Schedule, and a held seat went too
    //   'seat'              the seat button was pressed, which removes both
    //
    // The last two describe the same end state and differ only in emphasis —
    // they follow the button the attendee actually pressed. The board carries
    // both, which is why "Remove From My Schedule" appears twice on it.
    variant: {
        type: String,
        default: 'schedule',
        validator: (v) => ['schedule', 'schedule-and-seat', 'seat'].includes(v),
    },
    // No conflicts left anywhere on the schedule.
    resolved: { type: Boolean, default: false },
});

const open = defineModel('open', { default: true });
const emit = defineEmits(['close']);

const result = ref(null);

const quoted = computed(() => `“${props.sessionTitle}”`);

// Verbatim from the comps — 4755:114090 / 4755:113197 (schedule) and
// 4755:113495 / 4755:113346 (seat). The two resolved lines word the same idea
// differently ("the issue has been resolved" vs "The issue is now resolved"),
// which is the designs' own wording, not a slip.
const headline = computed(() => {
    if (props.variant === 'seat') {
        return props.resolved
            ? `Your saved seat for ${quoted.value} was removed and the session was removed from My Schedule. The issue is now resolved!`
            : `Your saved seat for ${quoted.value} was removed and the session was removed from My Schedule, but action is still needed.`;
    }
    // 4755:113784 / 4755:113663 — same end state as 'seat', led with the schedule
    // because that is the button that was pressed.
    if (props.variant === 'schedule-and-seat') {
        return props.resolved
            ? `${quoted.value} was removed from My Schedule and your saved seat was also removed. The issue is now resolved!`
            : `${quoted.value} was removed from My Schedule and your saved seat was also removed, but action is still needed.`;
    }
    return props.resolved
        ? `${quoted.value} has been removed from My Schedule and the issue has been resolved!`
        : `${quoted.value} was removed from My Schedule, but action is still needed.`;
});

const choose = (value) => {
    result.value = value;
    open.value = false;
};

// Resolves through emit('close', value) like the other dialogs, so the opener
// owns what happens next and this stays presentational.
const onShellClose = () => emit('close', result.value);
</script>

<template>
    <FragmentsModalShell
        v-model:open="open"
        :title="headline"
        @close="onShellClose"
    >
        <div class="flex flex-col items-center gap-[45px] w-full">
            <h2 class="m-0 w-full text-center font-hubspot-serif font-normal text-[25px] sm:text-[30px] leading-[1.3] tracking-normal text-(--magenta)">
                {{ headline }}
            </h2>

            <!-- Still-clashing only: the shared notice box (4755:113209). -->
            <div
                v-if="!resolved"
                data-test="conflict-removed-notice"
                class="flex items-start gap-[14px] w-full text-left bg-white rounded-[10px] px-[28px] py-[25px]"
            >
                <svg class="shrink-0 mt-[4px] size-[16px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="currentColor" />
                    <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                    <circle cx="8" cy="11.6" r="0.9" fill="white" />
                </svg>
                <p class="m-0 font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--magenta)">
                    There are still conflicting items on My Schedule that require action.
                </p>
            </div>

            <div class="flex items-start gap-[20px]">
                <template v-if="resolved">
                    <!-- Nothing left to do, so Close is the only action and it is
                         a button here rather than the text link the other state
                         uses (4755:114113). -->
                    <button
                        type="button"
                        data-test="conflict-removed-close"
                        :class="modal_button_outline_2026"
                        @click="choose(null)"
                    >Close</button>
                </template>
                <template v-else>
                    <button
                        type="button"
                        data-test="conflict-removed-fix"
                        :class="modal_button_filled_2026"
                        @click="choose('fix')"
                    >Take Me to Fix This</button>
                    <button
                        type="button"
                        data-test="conflict-removed-learn"
                        :class="modal_button_outline_2026"
                        @click="choose('learn')"
                    >Learn More</button>
                </template>
            </div>
        </div>

        <!-- The still-clashing state keeps the text-link Close BELOW its two
             actions (4755:113228); the resolved state has no second dismissal. -->
        <template v-if="!resolved" #actions>
            <button
                type="button"
                data-test="conflict-removed-dismiss"
                :class="modal_close_link_2026"
                @click="choose(null)"
            >Close</button>
        </template>
    </FragmentsModalShell>
</template>
